import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../utils/axiosInstance";

export const extractUniqueFeatureIds = (groups: any) => {
  const allIds = groups.flatMap((group: any) =>
    group.features
      .filter((feature: any) => feature.isChecked)
      .map((feature: any) => feature.id),
  );
  return Array.from(new Set(allIds));
};

export const transformFeatures = (data: any, apiResult: any) => {
  return data
    .flatMap((group: any) =>
      group.features
        .filter((feature: any) => feature.isChecked)
        .map((feature: any) => ({
          id: feature.id,
          groupId: group.groupId,
          name: feature.featureName,
          featureDescription: feature.featureDescription,
          groupName: group.groupName,
          associatedPermissions: apiResult.data
            .filter((permission: any) => permission.featureId === feature.id)
            .map((permission: any) => ({
              id: permission.permissionId,
              optionLabel: permission.permissionName,
            })),
          open: false,
        })),
    )
    .sort((a: any, b: any) => a.groupName.localeCompare(b.groupName));
};

// const transformSelectedPermissions = (selectedPermissions: any[]) => {
//   return selectedPermissions.flatMap(
//     ({ id: featureId, permissions, justification }) =>
//       permissions.map(({ id: permissionId }: { id: number }) => ({
//         permissionId,
//         featureId,
//         justification,
//       })),
//   );
// };

// Async thunk to fetch feature permissions
export const fetchFeaturePermissionData = createAsyncThunk(
  "featurePermission/fetchData",
  async (_, { getState }) => {
    const state: any = getState();
    const featureGroups = state.featureGroupWithFeature.data;

    const uniqueFeatureIds = extractUniqueFeatureIds(featureGroups);
    if (uniqueFeatureIds?.length === 0) {
      return [];
    }

    const response = await axiosInstance.post(
      "rolemanager/featureWithPermission",
      { featureIds: uniqueFeatureIds },
    );

    if (response.status === 200) {
      return transformFeatures(featureGroups, response.data);
    }
    throw new Error("Failed to fetch feature permissions");
  },
);

// Async thunk to submit feature permissions
export const submitFeaturePermissionData = createAsyncThunk(
  "featurePermission/submitData",
  async (
    params: {
      selectedPermissions: any[];
      userData: any[];
      validateBeforeSubmit: boolean;
    },
    { getState },
  ) => {
    const response = await axiosInstance.post("/request/create", {
      featurePermissionIds: params?.selectedPermissions || [],
      ...(params?.userData?.length > 0
        ? {
            user_data: params?.userData,
          }
        : {}),
      validateBeforeSubmit: params?.validateBeforeSubmit,
    });

    if (response.data.status.toLowerCase() === "success") {
      return response.data;
    }
    if (response.data.status.toLowerCase() === "failure") {
      return response.data;
    }
  },
);

export interface FeaturePermissionEntry {
  featureId: number;
  permission: number[];
  justification: string;
}

// Initial state
export interface FeaturePermissionState {
  data: any[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  submitStatus: "idle" | "loading" | "succeeded" | "failed";
  submitError: string | null;
  featurePermissionState: FeaturePermissionEntry[];
  clonedFeatureIds: number[];
}

const initialState: FeaturePermissionState = {
  data: [],
  status: "idle",
  error: null,
  submitStatus: "idle",
  submitError: null,
  featurePermissionState: [],
  clonedFeatureIds: [],
};

// Slice
const featurePermissionSlice = createSlice({
  name: "featurePermission",
  initialState,
  reducers: {
    resetSubmitStatus: (state) => {
      state.submitStatus = "idle";
      state.submitError = null;
    },
    setClonedFeatureIds: (state, action: PayloadAction<number[]>) => {
      state.clonedFeatureIds = action.payload;
    },
    setFeaturePermissionState: (
      state,
      action: PayloadAction<FeaturePermissionEntry[]>,
    ) => {
      state.featurePermissionState = action.payload;
    },
    updateFeaturePermissionEntry: (
      state,
      action: PayloadAction<FeaturePermissionEntry>,
    ) => {
      const index = state.featurePermissionState.findIndex(
        (item) => item.featureId === action.payload.featureId,
      );
      if (index !== -1) {
        state.featurePermissionState[index] = action.payload;
      } else {
        state.featurePermissionState.push(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch feature permissions
      .addCase(fetchFeaturePermissionData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(
        fetchFeaturePermissionData.fulfilled,
        (state, action: PayloadAction<any[]>) => {
          state.status = "succeeded";
          state.data = action.payload;
        },
      )
      .addCase(fetchFeaturePermissionData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to load permissions";
      })
      // Submit feature permissions
      .addCase(submitFeaturePermissionData.pending, (state) => {
        state.submitStatus = "loading";
      })
      .addCase(submitFeaturePermissionData.fulfilled, (state) => {
        state.submitStatus = "succeeded";
      })
      .addCase(submitFeaturePermissionData.rejected, (state, action) => {
        state.submitStatus = "failed";
        state.submitError = action.error.message
          ? action.error.message
          : "No Role Exists for some/all selected feature permission combinations";
      });
  },
});
export const {
  resetSubmitStatus,
  setFeaturePermissionState,
  updateFeaturePermissionEntry,
  setClonedFeatureIds,
} = featurePermissionSlice.actions;
export default featurePermissionSlice.reducer;
